/**
 * OF-001D — Canonical "My Dashboard" route resolver (hardened build).
 *
 * Single source of truth for any UI control labeled "My Dashboard"
 * (Header, LandingPage, etc.).  Decision rules — per charter §5 / §7:
 *
 *   1. If a valid `activeWorkspace` object is set AND available AND
 *      has a `default_route` → return that.
 *   2. **Anti-flicker guard.** If `activeWorkspace` is null/undefined
 *      (transient React state during a `setWorkspaces`/`refreshProfile`
 *      reconciliation race) but `activeWorkspaceId` is provided (or
 *      can be read from `localStorage.treatx_last_active_workspace`),
 *      look that id up directly in `workspaces` and return its
 *      `default_route`.  This defends against the documented
 *      OWNER VALIDATION FAILURE — "My Dashboard navigates to Patient
 *      while workspace label still shows Sales".
 *   3. Else (no active workspace at all) → first available
 *      workspace's `default_route`.
 *   4. Else (no workspaces, unauthenticated) → `/patient/dashboard`
 *      (public default; route guards bounce to /login if needed).
 *
 * The resolver NEVER silently returns `/patient/dashboard` when an
 * authenticated provider workspace is the persisted active selection.
 */

const PATIENT_FALLBACK = '/patient/dashboard';
const ACTIVE_WORKSPACE_STORAGE_KEY = 'treatx_last_active_workspace';

function readPersistedActiveWorkspaceId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function isUsable(ws) {
  return !!(ws && ws.default_route && ws.is_available !== false);
}

export function resolveDashboardRouteForActiveWorkspace(
  activeWorkspace,
  workspaces,
  activeWorkspaceId
) {
  // 1. Active workspace object wins.
  if (isUsable(activeWorkspace)) {
    return activeWorkspace.default_route;
  }

  const list = Array.isArray(workspaces) ? workspaces : [];

  // 2. Anti-flicker — explicit id (param) OR persisted id (localStorage).
  const persistedId = activeWorkspaceId || readPersistedActiveWorkspaceId();
  if (persistedId) {
    const persisted = list.find(
      (w) => w && w.workspace_id === persistedId && isUsable(w)
    );
    if (persisted) return persisted.default_route;
  }

  // 3. First available workspace.
  const candidate = list.find((w) => isUsable(w));
  if (candidate) return candidate.default_route;

  // 4. Public fallback.
  return PATIENT_FALLBACK;
}

/**
 * OF-001D — Phase C — Route/Workspace consistency resolver.
 *
 * Given the current pathname and the user's workspace catalogue,
 * return the workspace that "owns" that path — i.e. the workspace
 * whose `default_route` is the longest prefix match against
 * `pathname`.  Used by `WorkspaceRouteSync` to keep the header
 * label and the rendered page in lockstep when the user lands on
 * a deep link belonging to a workspace other than the currently
 * active one.
 *
 * Returns `null` when no available workspace claims the path, or when
 * the claim is AMBIGUOUS (several workspaces own the same route and the
 * active provider does not disambiguate them) — automatic selection is
 * then not permitted (TREATX-UAT-PROD-CONTEXT-SURFACE-01c).
 * Matching rules:
 *   - workspace must be available (`is_available !== false`)
 *   - workspace must have a `default_route`
 *   - either `pathname === default_route` OR
 *     `pathname.startsWith(default_route + "/")`
 *   - longest `default_route` wins.
 */
export function findWorkspaceOwningRoute(pathname, workspaces, activeClinicId = null) {
  if (!pathname || !Array.isArray(workspaces)) return null;
  // MTMR-P0 — with multi-provider memberships several workspaces can
  // share one default_route (e.g. Sales @ Provider A and Sales @
  // Provider B both own `/clinic/sales`). Route ownership alone can no
  // longer identify the ACTIVE workspace, so the active provider wins.
  //
  // TREATX-UAT-PROD-CONTEXT-SURFACE-01c (Codex re-review) — when the
  // longest-prefix owner is still AMBIGUOUS (several workspaces own the
  // same route and the active provider does not disambiguate them) this
  // helper MUST return null. Picking by catalogue order is the same
  // "one candidate ⇒ one context" defect as `length === 1`: it silently
  // minted Provider A's context for an account that also holds Provider
  // B. No automatic selection is allowed there — the caller renders the
  // explicit selection surface instead.
  const clinicId = activeClinicId || (() => {
    try { return sessionStorage.getItem('treatx_active_clinic_id'); } catch { return null; }
  })();
  let bestLen = -1;
  let candidates = [];
  for (const ws of workspaces) {
    if (!isUsable(ws)) continue;
    const dr = ws.default_route;
    if (pathname === dr || pathname.startsWith(dr + '/')) {
      if (dr.length > bestLen) {
        bestLen = dr.length;
        candidates = [ws];
      } else if (dr.length === bestLen) {
        candidates.push(ws);
      }
    }
  }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  if (clinicId) {
    const scoped = candidates.filter((ws) => ws.clinic_id === clinicId);
    if (scoped.length === 1) return scoped[0];
  }
  return null;   // ambiguous — the user must choose explicitly
}

/**
 * Convenience reader that pulls the active workspace + catalogue from
 * the canonical localStorage keys.  Useful in components that can't
 * (or shouldn't) take a hard dependency on `RBACContext` — e.g. a
 * legacy Header rendered above the provider tree.
 */
export function resolveDashboardRouteFromStorage() {
  let active = null;
  let workspaces = [];
  let activeId = null;
  try {
    workspaces = JSON.parse(localStorage.getItem('treatx_rbac_workspaces') || '[]') || [];
    activeId = readPersistedActiveWorkspaceId();
    if (activeId && Array.isArray(workspaces)) {
      active = workspaces.find((w) => w && w.workspace_id === activeId) || null;
    }
  } catch { /* ignore */ }
  return resolveDashboardRouteForActiveWorkspace(active, workspaces, activeId);
}
